import React from 'react'
import Image from '../../imgix-img'
import ProfileInformation from '../form-helpers/profile-information'
import { useForm } from 'react-hook-form'
import { useComponentTracking } from '../../../utils/component-events'
import { maskPII } from '../../../utils/form'
import { Button, Field, Input, Label, Select } from '../../../atoms'
import { setConfig } from '../../helpers/consent'
import DirectorFinder, { DirectorDetails } from '../form-helpers/director-details'
import { readCookieRaw } from '../../../utils/cookies'
import ProgressCircle from '../../form/progress-circle'
import { connect } from 'react-redux'
import { getComponentById } from '../../../redux/component/component-selectors'
import { FsCore } from 'types/fs-core'
import { brand } from '../../../redux/app-selectors'
import ProductDetails from '../../product-details'
import HomeAddress from '../form-helpers/home-address'
import { fetchWithRetry } from '../../../utils/fetch-with-retry'
import uuid from '../../../utils/guid'
import { NumberInput } from '../form-helpers/number-input'
import { fetchProfile, piiData, prefillFormData, ProfileFormData } from '../form-helpers/utils'
import Prefill from '../form-helpers/prefill'
import InEligible from '../form-helpers/ineligible'
import { isEmpty } from 'lodash-es'

interface CapitalontapFormProps {
  brand: FsCore.FSBrand
}

const { __FS_API_URL__ } = global
const apiPath: string = __FS_API_URL__ || 'https://www.money.co.uk/api'

const formName = 'Capital on tap form'

const CapitalontapForm: React.FC<CapitalontapFormProps> = ({ brand, productData, schema }) => {
  const [showBusinessDetailsSection, setShowBusinessDetailsSection] = React.useState(false)
  const [consents, setConsents] = React.useState({ marketing: true })
  const [businessDirectors, setBusinessDirectors] = React.useState<DirectorDetails[]>([])
  const [validationError, setValidationError] = React.useState<string | null>(null)
  const [submitLoading, setSubmitLoading] = React.useState(false)
  const [prefilled, setPrefilled] = React.useState({ prefilled: false, loading: false })
  const [prefillProfile, setPrefillProfile] = React.useState<ProfileFormData | null>(null)

  const trackComponent: (
    eventName: string,
    subEventName: string,
    payload: Record<string, any>,
  ) => void = useComponentTracking()

  const trackFormFieldInteracted = (detail: string, value: any) => {
    return trackComponent('Form Event', 'formFieldInteracted', {
      formStep: detail,
      selected: piiData.includes(detail) ? 'untracked' : value,
    })
  }

  React.useEffect(() => {
    setConfig({
      apiPath: window.location.href.includes('staging')
        ? 'https://www.staging.money.co.uk/consent'
        : 'https://www.money.co.uk/consent',
      clientId: 'fs-core',
      brand: 'money',
    })
    setSubmitLoading(false)
    setValidationError(null)
  }, [])

  const {
    register,
    errors,
    setValue,
    watch,
    unregister,
    handleSubmit,
    control,
    setError,
    clearErrors,
  }: { register: (validation: any) => any } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      businessDetails: {
        businessType: 'limitedCompany',
        tradingName: undefined,
      },
      directorDetails: {
        title: undefined,
        firstName: undefined,
        lastName: undefined,
        dateOfBirth: undefined,
        emailAddress: undefined,
        mobileNumber: undefined,
        nationality: undefined,
      },
      otherDetails: {
        fscsConsent: false,
        productConsent: false,
        marketingConsent: false,
        isSameHomeAddress: 'yes',
      },
    },
  })
  const companyNumber = watch('businessDetails.companyNumber')
  const directorFirstName = watch('directorDetails.firstName')
  const homeAddressEmpty = isEmpty(watch('directorDetails.address'))

  React.useEffect(() => {
    const smeSession = readCookieRaw('sme-session-id')
    if (smeSession) {
      setPrefilled({ loading: true, prefilled: false })
      fetchProfile()
        .then((profileData) => {
          setPrefillProfile(profileData)
          if (profileData.businessDetails.businessType === 'limitedCompany') {
            prefillFormData(profileData, setValue, register, trackFormFieldInteracted).then(() => {
              setPrefilled({ loading: false, prefilled: true })
              setShowBusinessDetailsSection(true)
              trackComponent('Form Event', 'prefillInfo', {
                FormName: formName,
                FieldData: maskPII(
                  { ...profileData.businessDetails, ...profileData.directorDetails },
                  piiData,
                ),
              })
            })
          } else {
            setPrefilled({ loading: false, prefilled: false })
          }
        })
        .catch((e) => {
          setPrefilled({ loading: false, prefilled: false })
          // eslint-disable-next-line no-console
          console.error(e.message)
        })
    }
  }, [])

  React.useEffect(() => {
    const fetchDirectorData = async () => {
      const res = await fetch(`/api/company/${companyNumber}/officers`)
      const json = await res.json()
      const directors = json.data.filter((d) => d.officer_role === 'director')
      setBusinessDirectors(directors)
    }
    if (companyNumber) {
      fetchDirectorData()
    }
  }, [companyNumber])

  const registerWithTrack = (validation: any, detail: any) => {
    return register({
      ...validation,
      validate: (value: any) => {
        trackFormFieldInteracted(detail, value)
        return true
      },
    })
  }

  const handleBusinessDetailsSubmit = async (finalBody: Object) => {
    const res = await fetch(
      `${apiPath}/sme-profile?brand=money&deviceId=${readCookieRaw('uscd')}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalBody),
      },
    )
    trackComponent('Form Event', 'sectionCompleted', {
      SectionName: 'Profile Information',
      FormName: formName,
      FieldData: maskPII(finalBody, ['businessAddress', 'email']),
      status: res.ok ? 'Success' : 'Failed',
    })
    setShowBusinessDetailsSection(true)
  }

  const helpHTML = () => {
    return (
      <div className='ProviderForm__typeOfBusinessHelp'>
        This credit card is for limited companies. Sole traders can find alternative products on our{' '}
        <a href='https://www.money.co.uk/business/business-credit-cards'>results page</a>.
      </div>
    )
  }
  const inEligibleProfile = !prefillProfile
    ? false
    : prefillProfile?.businessDetails?.businessType !== 'limitedCompany'
  return (
    <div className='ProviderForm'>
      <div className='SMEform'>
        <div>
          <h3>Apply for a Capital on Tap business credit card</h3>
          <div className='SMEform__logo'>
            <Image
              src={
                'https://img.money.co.uk/s3/uswitch-assets-eu/amp/images/provider/logos/Capital_on_Tap_logo.png'
              }
              height={66}
              width={180}
              critical={false}
              alt={'capital on tap Logo'}
            />
          </div>
        </div>
        {(prefilled.loading || prefilled.prefilled) && <Prefill loading={prefilled.loading} />}
        {inEligibleProfile && <InEligible setPrefillProfile={setPrefillProfile} />}
        {!inEligibleProfile && (
          <form onSubmit={(e) => e.preventDefault()} autoComplete='off'>
            <ProfileInformation
              registerWithTrack={registerWithTrack}
              register={register}
              setValue={setValue}
              unregister={unregister}
              businessDetails={watch('businessDetails')}
              directorDetails={watch('directorDetails')}
              hideButton={showBusinessDetailsSection}
              consents={consents}
              setConsents={setConsents}
              setError={setError}
              clearErrors={clearErrors}
              errors={errors}
              brand={brand}
              consentSource={'capitalontap-journey'}
              businessOptions={[{ name: 'limitedCompany', text: 'Limited Company' }]}
              companyFiltering={['ltd', 'llp']}
              formName={formName}
              ctaText='Continue to business details'
              handleSubmit={handleBusinessDetailsSubmit}
              control={control}
              TypeOfBusinessHelpHTML={helpHTML}
              defaultBusinessNameInput={watch('businessNameInput')}
            />
            {showBusinessDetailsSection ? (
              <>
                <div className='SMEform__companyDetails__detailsBox'>
                  <div className='SMEform__title__box'>
                    <Label className='SMEform__title'>Application details</Label>
                  </div>
                  <div className='SMEform__input'>
                    <Field>
                      <Label className={'required'}>
                        What is your turnover in the last 12 months (£)?
                      </Label>
                      <NumberInput
                        name='businessDetails.turnover'
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: 'Please enter the annual turnover of your business',
                          },
                          min: {
                            value: 10,
                            message: 'Minimum £10',
                          },
                        }}
                        trackField={(name: string, value: string) =>
                          trackComponent('Form Event', 'formFieldInteracted', {
                            formStep: name,
                            selected: value,
                          })
                        }
                        defaultValue={watch('businessDetails.turnover')}
                      />
                      {errors?.businessDetails?.turnover && (
                        <div className='SMEform__error'>
                          {errors.businessDetails.turnover.message}
                        </div>
                      )}
                    </Field>
                    <Field>
                      <Label className={'required'}>Repayment option</Label>
                      <Select
                        name='businessDetails.repaymentOptions'
                        options={[
                          { text: 'Please Select', value: '' },
                          { text: 'Full balance', value: 'fullbalance' },
                          { text: 'Minimum', value: 'minimum' },
                        ]}
                        onChange={(e) => {
                          setValue('businessDetails.repaymentOptions', e.target.value)
                        }}
                        value={watch('businessDetails.repaymentOptions') ?? ''}
                        ref={registerWithTrack(
                          {
                            required: {
                              value: true,
                              message: 'Please tell us how you would like to repay your loan',
                            },
                          },
                          'repaymentOptions',
                        )}
                      />
                      {errors?.businessDetails?.repaymentOptions && (
                        <div className='SMEform__error'>
                          Please tell us how you would like to repay your loan
                        </div>
                      )}
                    </Field>
                    <Field>
                      <Label className={'required'}>
                        Ability to send cash from facility to bank account
                      </Label>
                      <Select
                        name='businessDetails.canSendCashFromCreditFacilityToBank'
                        options={[
                          { text: 'Please Select', value: '' },
                          { text: 'Yes', value: 'yes' },
                          { text: 'No', value: 'no' },
                        ]}
                        onChange={(e) => {
                          setValue(
                            'businessDetails.canSendCashFromCreditFacilityToBank',
                            e.target.value,
                          )
                        }}
                        value={watch('businessDetails.canSendCashFromCreditFacilityToBank') ?? ''}
                        ref={registerWithTrack(
                          {
                            required: {
                              value: true,
                              message:
                                'Please tell us whether you can send cash from facility to bank account',
                            },
                          },
                          'canSendCashFromCreditFacilityToBank',
                        )}
                      />
                      {errors?.businessDetails?.canSendCashFromCreditFacilityToBank && (
                        <div className='SMEform__error'>
                          {errors.businessDetails.canSendCashFromCreditFacilityToBank.message}
                        </div>
                      )}
                    </Field>
                    <Field>
                      <Label className='required'>Trading name</Label>
                      <input
                        name='businessDetails.tradingName'
                        type='text'
                        value={watch('businessDetails.tradingName') ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue('businessDetails.tradingName', e.target.value)
                        }}
                        ref={registerWithTrack(
                          {
                            required: "Please enter your company's trading name",
                            maxLength: {
                              value: 21,
                              message: 'Maximum 21 characters',
                            },
                            pattern: {
                              // eslint-disable-next-line no-useless-escape
                              value: /^[a-zA-Z0-9][a-zA-Z0-9 -&.,'\/()]+$/,
                              message: "Please enter your company's trading name",
                            },
                          },
                          'tradingName',
                        )}
                      />
                      {errors?.businessDetails?.tradingName && (
                        <div className='SMEform__error'>
                          {errors.businessDetails.tradingName.message}
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
                <DirectorFinder
                  directorDetails={watch('directorDetails', {})}
                  businessDirectors={businessDirectors}
                  registerWithTrack={registerWithTrack}
                  register={register}
                  setValue={setValue}
                  errors={errors}
                  setError={setError}
                  clearErrors={clearErrors}
                />

                {directorFirstName && (
                  <HomeAddress
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    setError={setError}
                    clearErrors={clearErrors}
                    addressFieldName={'directorDetails.address'}
                  />
                )}
                <div className='SMEform__productDetailsMobile'>
                  <ProductDetails productData={productData} schema={schema} />
                </div>
                <Field className='SMEform__typeOfBusiness'>
                  <Label>Review Terms</Label>
                  <div className='SMEform__input'>
                    <label className='SMEform__fscsConsent'>
                      <Input
                        type='checkbox'
                        name='otherDetails.productConsent'
                        ref={registerWithTrack(
                          {
                            required: 'Please review and accept the product terms',
                          },
                          'productConsent',
                        )}
                      />
                      <div>
                        I have read and understand the{' '}
                        <a
                          href='https://www.capitalontap.com/en/legal/privacy-policy/'
                          target='blank'
                        >
                          privacy policy
                        </a>{' '}
                        which explains how Capital On Tap will use my personal and business
                        information.
                      </div>
                    </label>

                    <div className='SMEform__fscsConsent__small'>
                      Applying will have no impact on your personal credit file. Capital On Tap will
                      conduct a soft search on your personal credit file and a hard search on your
                      business profile.
                    </div>

                    {(errors?.otherDetails?.productConsent ||
                      errors?.otherDetails?.fscsConsent) && (
                      <div className='SMEform__error'>Please tick all the checkboxes above</div>
                    )}
                    <div className='SMEform__fscsConsent'>
                      <p>
                        We will show you a range of products from our partners. View their{' '}
                        <a href='https://www.money.co.uk/privacy-notice/partners' target='blank'>
                          privacy policies
                        </a>{' '}
                        for more information.
                      </p>
                    </div>
                  </div>
                </Field>
                <div className='SMEform__buttonWrapper'>
                  {submitLoading ? (
                    <div className='SMEform__loadingMessage'>
                      <div className='SMEform__loadingMessage__spinner'>
                        <ProgressCircle height={48} />
                      </div>
                      <p>
                        Bear with us, Capital on Tap is currently reviewing the information you
                        provided. Thank you for your patience, please do not refresh or hit back.
                      </p>
                    </div>
                  ) : (
                    <Button
                      onClick={handleSubmit(async (data) => {
                        const { businessDetails, directorDetails } = data
                        const finalBody = {
                          ...(productData
                            ? {
                                selectedProduct: { id: productData._id, index: productData._index },
                              }
                            : {}),
                          registeredBusinessAddress: businessDetails.address,
                          businessName: businessDetails.businessName,
                          companyType: businessDetails.companyType,
                          businessType: businessDetails.businessType,
                          dateOfBirthDay: directorDetails.dateOfBirthFields.day.replace(
                            /^(?:0+(?=[1-9])|0+(?=0$))/gm,
                            '',
                          ),
                          dateOfBirthMonth: directorDetails.dateOfBirthFields.month.replace(
                            /^(?:0+(?=[1-9])|0+(?=0$))/gm,
                            '',
                          ),
                          dateOfBirthYear: directorDetails.dateOfBirthFields.year,
                          emailAddress: directorDetails.emailAddress,
                          firstName: directorDetails.firstName,
                          lastName: directorDetails.lastName,
                          mobileNumber: directorDetails.mobileNumber,
                          turnover: parseInt(businessDetails.turnover),
                          homeAddress: directorDetails.address?.postcode
                            ? directorDetails.address
                            : businessDetails.address,
                          companyRegistrationNumber: businessDetails.companyNumber,
                          title: directorDetails.title,
                          tradingName: businessDetails.tradingName,
                          repaymentOptions: businessDetails.repaymentOptions,
                          canSendCashFromCreditFacilityToBank:
                            businessDetails.canSendCashFromCreditFacilityToBank === 'yes',
                          date_of_creation: businessDetails.date_of_creation,
                          dateOfCreation: businessDetails.dateOfCreation,
                        }
                        try {
                          setSubmitLoading(true)
                          setValidationError(null)
                          trackComponent('Form Event', 'sectionCompleted', {
                            SectionName: 'Application Form Information',
                            FormName: formName,
                            FieldData: maskPII(finalBody, piiData),
                          })
                          trackComponent('Form Event', 'formSubmitted', {
                            formName: formName,
                          })
                          const res = await fetch(
                            `${apiPath}/cot/application?brand=money&requestId=${uuid()}&deviceId=${readCookieRaw(
                              'uscd',
                            )}`,
                            {
                              method: 'POST',
                              body: JSON.stringify(finalBody),
                              headers: {
                                'Content-Type': 'application/json',
                              },
                            },
                          )
                          if (res.ok) {
                            const res = await fetchWithRetry(
                              `${apiPath}/cot/application?brand=money&check=true&deviceId=${readCookieRaw(
                                'uscd',
                              )}`,
                              300000,
                            )
                            if (res === undefined || !res.ok || res.status === 202) {
                              setValidationError(
                                `Oops looks like something went wrong. Timeout Error`,
                              )
                              setSubmitLoading(false)
                            } else {
                              const response = await res.json()
                              if (response.status === 'Declined') {
                                window.location.href = '/business/capital-on-tap/no-results'
                              } else if (response.status !== 'Error') {
                                window.location.href = '/business/capital-on-tap/results'
                              } else {
                                setValidationError('Oops looks like something went wrong.')
                                setSubmitLoading(false)
                              }
                            }
                          } else {
                            const result = await res.json()
                            if (result.errors && Array.isArray(result.errors)) {
                              setValidationError(
                                `Oops looks like something went wrong. ${JSON.stringify(
                                  result.errors?.[0]?.message ?? result.errors,
                                )}`,
                              )
                            } else {
                              setValidationError(
                                `Oops looks like something went wrong. ${JSON.stringify(
                                  result.error,
                                )}`,
                              )
                            }
                            setSubmitLoading(false)
                          }
                        } catch (e) {
                          setValidationError(
                            `Oops looks like something went wrong. Please try again later. ${e}`,
                          )
                          setSubmitLoading(false)
                        }
                      })}
                      disabled={!directorFirstName || homeAddressEmpty}
                      variant='primary'
                      size='lg'
                      type='submit'
                    >
                      Submit
                    </Button>
                  )}
                </div>
                {validationError && (
                  <div className='SMEform__validationError'>{validationError}</div>
                )}
              </>
            ) : (
              <>
                <div className='SMEform__companyDetails__detailsBox'>
                  <div className='SMEform__title'>Application details</div>
                </div>
                <div className='SMEform__companyDetails__detailsBox'>
                  <div className='SMEform__title'>Director details</div>
                </div>
              </>
            )}
          </form>
        )}
      </div>
      <div className='SMEform__productDetailsDesktop'>
        <ProductDetails productData={productData} schema={schema} />
      </div>
    </div>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    brand: brand(state),
    productData: getComponentById(state, props.sys.id).productData,
    ...props,
  }
})(CapitalontapForm)
